import { StatusId, OperationBC, CommandeRang } from '../types';

// Les rangs d'une operation BC sont ceux de SES commandes, jamais la suite 1, 2, 3.
//
// POURQUOI : la base porte deja des operations sans rang 1 (lot dont seul le rang 2 est titulaire,
// commande complementaire creee pour le seul titulaire retenu, jeu {1,3}). Toute regle ecrite avec
// `rang - 1`, `rang === 1` ou une boucle 1..3 se trompe sur ces operations : elle affiche une
// "Consultation Rang 1" qui n'existe pas, ou cache le bouton de passage au rang 3 parce qu'elle
// cherche un rang 2 absent. Ce module est la seule source des notions de "premier rang", de "rang
// precedent" et de "rang courant" ; le reste du domaine s'y branche.

export const parseRang = (value: string | number | undefined): number => parseInt(String(value), 10);

/** Commandes de l'operation par rang croissant. Une commande sans rang lisible est ignoree. */
export const commandesParRang = (operation: OperationBC): CommandeRang[] =>
  (operation.commandes || [])
    .filter((c) => !Number.isNaN(parseRang(c.rang)))
    .slice()
    .sort((a, b) => parseRang(a.rang) - parseRang(b.rang));

/** Rangs distincts de l'operation, croissants. Interne : le domaine raisonne sur les commandes. */
const rangsDeLOperation = (operation: OperationBC): number[] =>
  Array.from(new Set(commandesParRang(operation).map((c) => parseRang(c.rang))));

/** Commande d'un rang : elle porte le titulaire de CE rang, son contrat et ses articles. */
export const commandeDuRang = (operation: OperationBC, rang: number): CommandeRang | undefined =>
  (operation.commandes || []).find((c) => parseRang(c.rang) === rang);

/**
 * Premier rang de l'operation : celui par lequel la consultation commence. Repli sur 1 pour une
 * operation sans aucune commande (jamais observe en base), afin que les onglets aient un rang
 * exploitable plutot qu'un `undefined` a propager.
 */
export const premierRang = (operation: OperationBC): number => rangsDeLOperation(operation)[0] ?? 1;

export const estPremierRang = (operation: OperationBC, rang: number): boolean =>
  premierRang(operation) === rang;

/** Commande du rang qui precede `rang` DANS L'OPERATION (et non `rang - 1`). Undefined pour le premier. */
export const commandePrecedente = (operation: OperationBC, rang: number): CommandeRang | undefined => {
  const precedentes = commandesParRang(operation).filter((c) => parseRang(c.rang) < rang);
  return precedentes.length ? precedentes[precedentes.length - 1] : undefined;
};

/** Rang ecarte de la cascade : refus ou expiration, les deux ouvrent le rang suivant. */
export const estRangEcarte = (idStatus?: number): boolean =>
  idStatus === StatusId.Refuse || idStatus === StatusId.Expire;

/**
 * Rang en cours de traitement : le premier, dans l'ordre des rangs, que son titulaire n'a ni
 * refuse ni laisse expirer (un rang accepte est donc le rang courant). Quand tous ont ete ecartes,
 * le dernier consulte fait foi : c'est lui qui a clos l'operation.
 * Remplace `operation.rang_actuel`, qu'aucune API (bdc, core) ne renvoie : le front le lisait a 1
 * en toutes circonstances.
 */
export const rangCourant = (operation: OperationBC): number => {
  const commandes = commandesParRang(operation);
  const enCours = commandes.find((c) => !estRangEcarte(c.id_status));
  const cible = enCours ?? commandes[commandes.length - 1];
  return cible ? parseRang(cible.rang) : premierRang(operation);
};
